<template>
    <div class="ma-4">
        <v-snackbar
            v-model="msg"
            top
            :color="snackbarColor"
            :timeout="3000"
            elevation="4"
            class="rounded-lg"
        >
            <div class="d-flex align-center">
                <v-icon class="mr-2">{{
                    snackbarColor === "success" ? "mdi-check-circle" : "mdi-alert"
                }}</v-icon>
                {{ text }}
            </div>
            <template v-slot:action="{ attrs }">
                <v-btn icon small v-bind="attrs" @click="msg = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </template>
        </v-snackbar>

        <v-card class="rounded-lg">
            <v-app-bar flat dense color="grey lighten-4" class="rounded-t-lg px-3">
                <v-btn icon small class="mr-2 grey lighten-3" @click="$router.go(-1)">
                    <v-icon>navigate_before</v-icon>
                </v-btn>

                <span class="text-h6 font-weight-medium grey--text text--darken-3">
                    MAR Report
                </span>

                <v-spacer></v-spacer>

                <v-btn
                    fab
                    small
                    @click="printTable()"
                    class="mr-2 white--text"
                    color="primary"
                    elevation="2"
                >
                    <v-icon>print</v-icon>
                </v-btn>
            </v-app-bar>

            <v-divider></v-divider>

            <v-card-text class="pa-6 grey lighten-5">
                <v-row dense>
                    <v-col cols="12" md="3">
                        <v-menu
                            ref="calendarMenu"
                            v-model="calendarMenu"
                            :close-on-content-click="false"
                            :return-value.sync="date"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="filters.startDate"
                                    label="Start Date"
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                    :rules='requiredField'
                                    clearable
                                    outlined
                                    dense
                                    hide-details
                                    class="rounded-lg"
                                    background-color="white"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="filters.startDate"
                                no-title
                                scrollable
                                :rules='requiredField'
                                class="rounded-lg"
                            >
                                <v-spacer></v-spacer>
                                <v-btn
                                    text
                                    color="primary"
                                    @click="calendarMenu = false"
                                >
                                    Cancel
                                </v-btn>
                                <v-btn
                                    text
                                    color="primary"
                                    @click="$refs.calendarMenu.save(date)"
                                >
                                    OK
                                </v-btn>
                            </v-date-picker>
                        </v-menu>
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-menu
                            ref="calendar2Menu"
                            v-model="calendar2Menu"
                            :close-on-content-click="false"
                            :return-value.sync="date"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="filters.endDate"
                                    label="End Date"
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                    :rules='requiredField'
                                    clearable
                                    outlined
                                    dense
                                    hide-details
                                    class="rounded-lg"
                                    background-color="white"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="filters.endDate"
                                no-title
                                scrollable
                                :rules='requiredField'
                                class="rounded-lg"
                            >
                                <v-spacer></v-spacer>
                                <v-btn
                                    text
                                    color="primary"
                                    @click="calendar2Menu = false"
                                >
                                    Cancel
                                </v-btn>
                                <v-btn
                                    text
                                    color="primary"
                                    @click="$refs.calendar2Menu.save(date)"
                                >
                                    OK
                                </v-btn>
                            </v-date-picker>
                        </v-menu>
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-select
                            @change="getMars()"
                            label="Select a Client"
                            :items="clients"
                            item-text="Name"
                            item-value="_id"
                            v-model="filters.ClientID"
                            clearable
                            outlined
                            dense
                            hide-details
                            class="rounded-lg"
                            background-color="white"
                        ></v-select>
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-btn
                            color="primary"
                            block
                            height="40"
                            class="rounded-lg"
                            :loading="isLoading"
                            elevation="1"
                            @click="getMars()"
                        >
                            <v-icon left>mdi-magnify</v-icon>
                            Search
                        </v-btn>
                    </v-col>
                </v-row>

                <v-row dense class="mt-4">
                    <v-col cols="12" md="3">
                        <v-menu offset-y v-if="filters.startDate && filters.endDate && filters.ClientID">
                            <template v-slot:activator="{ attrs, on }">
                                <v-btn
                                    class="white--text rounded-lg"
                                    v-bind="attrs"
                                    v-on="on"
                                    color="blue-grey"
                                    :loading="isDownloading"
                                    :disabled="isDownloading"
                                    elevation="1"
                                >
                                    Export
                                    <v-icon right dark> mdi-cloud-download</v-icon>
                                </v-btn>
                            </template>

                            <v-list class="pa-0 rounded-lg">
                                <v-list-item
                                    v-for="(item, index) in reportItems"
                                    :key="index"
                                    link
                                >
                                    <v-list-item-title
                                        v-text="item"
                                        @click="download(item)"
                                    ></v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </v-col>
                </v-row>
            </v-card-text>

            <!-- Data Table -->
            <v-card-text class="pa-6">
                <v-data-table
                    :page="page"
                    :pageCount="pageCount"
                    :server-items-length="itemCount"
                    :options.sync="options"
                    :headers="headers"
                    :items="mars"
                    sort-by="Name"
                    sort-desc
                    class="elevation-1 rounded-lg"
                    :loading="isLoading"
                    loading-text="Loading MAR"
                    :item-class="rowColor"
                    :footer-props="{
                        'items-per-page-options': [5, 10, 20, 50],
                        showFirstLastPage: true,
                        'items-per-page-text': 'Rows per page:',
                        class: 'rounded-lg',
                    }"
                >
                    <template v-slot:item.select="{ item }">
                        <v-btn
                            x-small
                            color="primary"
                            @click="viewMAR(item)"
                            class="px-3"
                        >
                            <v-icon left x-small>mdi-eye</v-icon>
                            View
                        </v-btn>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <v-dialog v-model="dialog" max-width="80%" class="rounded-lg">
            <v-card class="rounded-lg">
                <v-card-title class="primary white--text">
                    <span class="text-h6">{{ titleForm }}</span>
                </v-card-title>
                <v-card-text class="pt-6">
                    <v-row>
                        <v-col
                            cols="12"
                            sm="6" md="4"
                        >
                            <v-text-field
                                class="mt-3 ml-3 rounded-lg"
                                label="Medication"
                                row-height="15"
                                v-model="medication"
                                readonly
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                        <v-col
                            cols="12"
                            sm="6" md="3"
                        >
                            <v-text-field
                                class="mt-3 mr-3 rounded-lg"
                                label="Amount Administered"
                                rows="1"
                                row-height="15"
                                v-model="amountAdministered"
                                readonly
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                    
                        <v-col
                            cols="12"
                            sm="6" md="4"
                        >
                            <v-text-field
                                class="mt-3 ml-3 rounded-lg"
                                label="Admin Time"
                                row-height="15"
                                v-model="adminTime"
                                readonly
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                        <v-col
                            cols="12"
                        >
                            <v-text-field
                                class="ma-3 rounded-lg"
                                label="Actual Admin Time"
                                v-model="actualAdminTime"
                                readonly
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                    
                        <v-col
                            cols="12"
                        >
                            <v-textarea
                                class="ma-3 rounded-lg"
                                label="Notes/Instructions"
                                auto-grow
                                outlined
                                row-height="15"
                                v-model="notes"
                                readonly
                            ></v-textarea>
                        </v-col>
                    
                        <v-col
                            cols="12"
                        >
                            <v-text-field
                                class="ma-3 rounded-lg"
                                label="Signature"
                                v-model="signature"
                                readonly
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                        
                        <v-col
                            cols="12"
                        >
                            <v-text-field
                                class="ma-3 rounded-lg"
                                label="Verified By:"
                                v-model="verifiedBy"
                                readonly
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                    
                        <v-col
                            cols="12"
                            sm="6"
                        >
                            <v-text-field
                                class="ma-3 rounded-lg"
                                label="Date"
                                v-model="marDate"
                                readonly
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                        <v-col
                            cols="12"
                            sm="6"
                        >
                            <v-text-field
                                class="ma-3 rounded-lg"
                                label="Exception"
                                v-model="exception"
                                readonly
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                        <v-col
                            cols="12"
                            sm="6" md="3"
                        >
                            <v-text-field
                                class="ma-4 rounded-lg"
                                label="Client Name"
                                v-model="clientID"
                                readonly
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                    
                        <v-col
                            cols="12"
                            sm="6" md="4"
                        >
                            <v-text-field
                                class="ma-3 rounded-lg"
                                label="Quantity"
                                v-model="quantity"
                                readonly
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                    
                        <v-col
                            cols="12"
                            sm="6" md="4"
                        >
                            <v-text-field
                                class="ma-3 rounded-lg"
                                label="Vitals Required"
                                v-model="vitalsRequired"
                                readonly
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                        <v-col
                            cols="12"
                            sm="6"
                        >
                            <v-text-field
                                class="ma-3 rounded-lg"
                                label="Follow Up PRN"
                                v-model="followUpPRN"
                                readonly
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn
                        fab
                        small
                        @click="showPrint()"
                        class="mr-2 white--text"
                        color="primary"
                        elevation="2"
                    >
                        <v-icon>print</v-icon>
                    </v-btn>
                    <v-btn
                        color="primary"
                        @click="close"
                        elevation="1"
                    >
                        Close
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <div id="printTable">
            <report-header>
                <h2>1 Month MAR Summary</h2>
            </report-header>
            <v-row row wrap>
                <v-col cols='12'>
                    <v-data-table
                        :headers="headers"
                        :items="mars"
                        sort-by="Date"
                        sort-asc
                        class="elevation-1"
                        :loading="isLoading"
                        loading-text="Loading MARS"
                        :items-per-page="-1"
                    >
                        <!-- <template v-slot:item.select="{ item }">
                            <v-btn
                                x-small
                                class="white--text"
                                color="#2b3990"
                                @click="viewMAR(item)"
                            >
                                view
                            </v-btn>
                        </template> -->
                    </v-data-table>
                </v-col>
            </v-row>
        </div>
    </div>
</template>
<script>
import axios from 'axios'
import moment from 'moment'
import {mapActions} from 'vuex';
import momentTz from 'moment-timezone';
import ReportHeader from '@/components/Report/ReportHeader.vue';
import PrintUtil from "../../../utils/print-util";
// import Foot from '../components/Footer'

const url = require("../../client_config/config").url;

export default {
    components: {
        ReportHeader
    },
    data: () => ({
        headers: [
            {
                text: 'Date',
                align: 'start',
                sortable: true,
                value: 'Date',
            },
            {text: "Client", value: "ClientID.Name"},
            {text: "Medication", value: "Medication.Medication"},
            {text: "RX", value: "Medication.RXNumber"},
            {text: "Scheduled Admin Time", value: "Medication.AdminTime"},
            {text: "Notes", value: "Notes"},
            {text: "Signature", value: "Signature"},
            {text: "", value: "select"},

        ],
        date: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
        clients: [],
        mars: [],
        programs: [],
        status: ['Complete', 'Incomplete'],
        calendarMenu: false,
        calendar2Menu: false,
        report: '',
        clientName: '',
        isLoading: false,
        isDownloading: false,
        program: "",
        userState: localStorage.getItem('userState'),
        token: localStorage.getItem('token'),
        page: 1,
        pageCount: 1,
        itemCount: 0,
        options: {},
        requiredField: [
            v => !!v || 'This is required',
        ],
        dialog: false,
        medication: '',
        amountAdministered: '',
        adminTime: '',
        actualAdminTime: '',
        notes: '',
        exception: '',
        signature: '',
        verifiedBy: '',
        marDate: '',
        clientID: '',
        staffID: '',
        quantity: '',
        followUpPRN: '',
        vitalsRequired: '',
        msg: false,
        titleForm: "",
        requiredRules: [v => !!v || 'This is required'],
        reportItems: ['PDF'],
        model: 'tab-2',
        text: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
        
        filters: {
            startDate: '',
            endDate: '',
            ClientID: '',
        },
        snackbarColor: "success",
    }),

    computed: {},

    watch: {
        options: {
            handler(newVal, oldVal) {
                // Only trigger search if we have filters set and this isn't the initial load
                if (oldVal !== newVal && Object.keys(oldVal).length > 0 && 
                    (this.filters.startDate || this.filters.endDate || this.filters.ClientID)) {
                    this.getMars();
                }
            },
            deep: true
        },
        
        filters: {
            handler() {
                this.setFilters();
            },
            deep: true
        }
    },

    created() {
        this.getClients();
        this.getFilters();
        //   this.getPrograms();
    },

    mounted() {
        window.addEventListener('keydown', async (event) => {
            const downloadUrl = await this.generateDownloadUrl();
            PrintUtil.handleKeydown(event, downloadUrl, this.marID, this.token,'MAR');
        });
    },

    beforeDestroy() {
        window.removeEventListener('keydown', (event) => {
            PrintUtil.handleKeydown(event, this.downloadUrl, this.marID, this.token, 'MAR');
        });
    },

    methods: {
        ...mapActions({
            downloadMARReport: "mar/downloadMARReport"
        }),
        initialize() {
            this.clients = [];
        },

        getClients() {
            // if(this.userState){
            //   return axios.get(`${url}/clients/state/${this.userState}`).then(async res => {
            //     this.clients = res.data.clients;
            //   });
            // }

            axios.get(`${url}/clients/status/state/${this.userState}`).then(async res => {
                this.clients = res.data.clients;
            });
        },

        //   getPrograms () {
        //     axios.get(`${url}/units/state/${this.userState}`).then(async res => {
        //       this.programs = res.data.units;

        //     });
        //   },

        close() {
            this.dialog = false;
            this.seditInfo = false;
            this.passdialog = false;
            this.chgimg = false;

        },

        // DEPRECATED CODE: TO BE REMOVED ONCE NEW IMPLEMENTATION IS FULLY TESTED
        // getMars() {
        //   let { page, itemsPerPage } = this.options;
        //   page = page || 1;
        //   itemsPerPage = 100;
        //   this.isLoading = true;

        //   axios.get(`${url}/MAR/report?page=${page}&limit=${itemsPerPage}&state=${this.userState}`).then(response => {
        //     const { docs, totalPages, totalDocs } = response.data;

        //     if(this.userState){
        //       if (docs && docs.length > 0) {
        //         this.mars = docs.filter(doc => this.userState === doc.ClientID.State).map(doc => {
        //           doc.Date = moment(doc.Date).format("MM/DD/YYYY");
        //           return doc;
        //         });
        //       }
        //     }else{
        //       this.mars = docs
        //     }


        //     this.pageCount = totalPages;
        //     this.itemCount = totalDocs;
        //     this.isLoading = false;
        //   })
        // },

        getMars() {
            let {page, itemsPerPage} = this.options;
            page = page || 1;
            itemsPerPage = itemsPerPage || 10;


            const params = {
                "startDate": this.filters.startDate || '',
                "endDate": this.filters.endDate || '',
                clientID: this.filters.ClientID || '',
                page: page,
                limit: itemsPerPage,
                tz: momentTz.tz.guess(),
                state: this.userState
            };

            let endpoint = `${url}/MAR/report`;

            this.isLoading = true;

            axios.get(endpoint, {params})
                .then(response => {
                    const {docs, totalPages, totalDocs} = response.data;

                    this.mars = docs.map((doc) => {
                        doc.Date = moment(doc.Date).tz('America/Denver').format("MM/DD/YYYY");
                        return doc;
                    })

                    this.pageCount = totalPages;
                    this.itemCount = totalDocs;
                })
                .finally(() => (this.isLoading = false))
        },

        // DEPRECATED CODE: TO BE REMOVED ONCE NEW IMPLEMENTATION IS FULLY TESTED
        // getQueryData() {
        //   this.isLoading = true
        //   console.log({id: this.ClientID})
        //   if(this.ClientID == ''){
        //     axios.get(`${url}/MAR/${this.startDate}/${this.endDate}`).then(res => {
        //       this.mars = res.data.mars
        //     })
        //     .finally(() => (this.isLoading = false));

        //   } else {
        //     axios.get(`${url}/MAR/${this.startDate}/${this.endDate}/${this.ClientID}`).then(res => {
        //       this.mars = res.data.mars

        //     })
        //     .finally(() => (this.isLoading = false));
        //   }

        // },

        downloadMarCSV() {
            const startDate = new Date(`${this.filters.startDate}`).toISOString()
            const endDate = new Date(`${this.filters.endDate}`).toISOString()
            window.location.href = `${url}/mar/download?clientId=${this.filters.ClientID}&startDate=${startDate}&endDate=${endDate}`;
        },

        async downloadMarPDF() {
            this.isDownloading = true
            const startDate = new Date(`${this.filters.startDate}`).toISOString()
            const endDate = new Date(`${this.filters.endDate}`).toISOString()
            const tz = momentTz.tz.guess();

            const data = {
                startDate,
                endDate,
                clientId: this.filters.ClientID,
                tz
            }
            try {
                const response = await this.downloadMARReport(data)
                if (response.status === 400) {
                    this.isDownloading = false
                    return alert('Unable to download report')
                }
                const blob = new Blob([response.data], {type: 'application/pdf'})
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', 'mar-summary-report.pdf')
                document.body.appendChild(link)
                link.click()
                this.isDownloading = false
            } catch (error) {
                this.isDownloading = false
                console.log(error)
            }

        },

        download(docs) {
            if (docs === 'PDF') {
                //Removed Excel/CSV functionality. Wasn't utilized.
                // this.downloadMarCSV()
                this.downloadMarPDF()
            }
        },

        getClientID(item) {
            this.client = item.ClientID._id;
            window.localStorage.setItem('clientId', this.client)
            console.log(this.client)
        },

        printTable() {

            window.print()

        },

        printItem() {
            document.getElementById('printTable').style.display = "none";

            window.print()
        },

        viewMAR(item) {
            this.titleForm = "Viewing MAR";
            this.dialog = true;
            this.marID = item._id;
            this.medication = item.Medication.Medication;
            this.amountAdministered = item.Medication.AdministerAmount;
            this.adminTime = item.Medication.AdminTime;
            this.actualAdminTime = item.ActualAdminTime;
            this.notes = item.Medication.MARInstructions;
            this.exception = item.Exception;
            this.signature = item.Signature;
            this.verifiedBy = item.VerifiedBy;
            this.marDate = item.Date;
            this.clientID = item.ClientID.Name;
            this.staffID = item.Medication.StaffID.Name;
            this.quantity = item.Medication.Quantity;
            this.followUpPRN = item.FollowUpPRN;
            this.vitalsRequired = item.Medication.VitalsRequired;
        },

        async generateDownloadUrl() {
            return `${url}/MAR/mar-report/print-pdf?id=${this.marID}`;
        },

        async showPrint() {
            const downloadUrl = await this.generateDownloadUrl();
            PrintUtil.showPrintOption(downloadUrl, this.marID, this.token, 'MAR');
        },

        rowColor(item) {
            return item.AdminTime == "PRN" ? "pink lighten-5" : "";
        },

        getFilters() {
            let filters = localStorage.getItem('MARReportFilter');
            if (filters) {
                this.filters = JSON.parse(filters);
            }
        },

        setFilters() {
            localStorage.setItem('MARReportFilter', JSON.stringify(this.filters));
        }
    }
}
</script>
<style scoped>
.v-data-table {
  border: 1px solid #e0e0e0;
}

.v-data-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.v-data-table ::v-deep tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.v-btn {
  text-transform: none;
  letter-spacing: 0;
}

.v-text-field ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.v-select ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.rounded-lg {
  border-radius: 8px !important;
}

.rounded-t-lg {
  border-top-left-radius: 8px !important;
  border-top-right-radius: 8px !important;
}
</style>

<style>
@media only screen and (min-width: 768px) {
    .mobile {
        display: none;
    }
}

@media only screen and (max-width: 600px) {
    .desktop {
        display: none;
    }

}

#printTable {
    display: none;
}

#printItem {
    display: none;
}

@media only screen and (min-width: 768px) {
    .mobile {
        display: none;
    }
}

@media only screen and (max-width: 600px) {
    .desktop {
        display: none;
    }

}

@media print {
    #printTable {
        display: block;
        border: none;
        width: 1300px;
        padding-right: 300px;
        margin-left: -200px;

    }

    .hideOnPrint {
        display: none;
        border: none;
    }

    nav.v-navigation-drawer, header.v-toolbar, button.v-btn, div.v-card {
        display: none;
        border: none;

    }

    @page {
        margin: 0;
    }

    body {
        margin: 1.6cm;
    }


}
</style>
